import { useMemo } from 'react';
import { Controller, useForm, useWatch, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { KeyRound, Loader2 } from 'lucide-react';
import { createUserAccountIdSchema, updateUserAccountIdSchema } from '@pms/shared';
import { getErrorMessage } from '@/lib/errors';
import {
  idManagementService,
  type IdCategory,
  type UserAccountId,
} from '@/services/id-management.service';
import type { PickerUser } from '@/services/users.service';
import { CategoryOption } from '@/components/id-management/CategoryIcon';
import { IdUserMultiPicker } from '@/components/id-management/IdUserMultiPicker';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const INACTIVE_DEPT = 'Inactive users';

interface FormValues {
  category_id: string;
  account_identifier: string;
  user_ids: string[];
}

interface Props {
  account?: UserAccountId;
  categories: IdCategory[];
  people: PickerUser[];
  onClose: () => void;
}

export function IdAccountFormDialog({ account, categories, people, onClose }: Props) {
  const queryClient = useQueryClient();

  const {
    register, control, handleSubmit, setValue, formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(account ? updateUserAccountIdSchema : createUserAccountIdSchema) as Resolver<FormValues>,
    defaultValues: {
      category_id: account?.category_id ?? '',
      account_identifier: account?.account_identifier ?? '',
      user_ids: account?.users.map((u) => u.id) ?? [],
    },
  });

  // The roster only lists active staff. Linked users who have since been deactivated are added back,
  // or saving would silently remove them.
  const options = useMemo(() => {
    const missing = (account?.users ?? [])
      .filter((u) => !people.some((p) => p.id === u.id))
      .map((u): PickerUser => ({
        id: u.id,
        employee_code: u.employee_code,
        first_name: u.name,
        last_name: '',
        department_name: INACTIVE_DEPT,
        is_active: false,
      }));
    return [...people, ...missing];
  }, [account, people]);

  const selectedIds = useWatch({ control, name: 'user_ids' });

  // The server trims and re-validates.
  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (account) {
        await idManagementService.updateAccount(account.id, values);
      } else {
        await idManagementService.createAccount(values);
      }
    },
    onSuccess: () => {
      toast.success(account ? 'Changes saved' : 'ID saved');
      void queryClient.invalidateQueries({ queryKey: ['id-management', 'accounts'] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      {/* Fixed header and footer with only the middle scrolling, the EditTaskModal pattern. The
          fixed md: height gives the user list a column to fill. */}
      <DialogContent className="flex max-h-[90vh] max-w-lg flex-col gap-0 overflow-hidden p-0 focus:outline-none sm:p-0 md:h-[660px] md:max-w-4xl">
        <DialogHeader className="shrink-0 border-b border-border/60 px-5 py-3 pr-10">
          <DialogTitle className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <KeyRound className="h-3.5 w-3.5" />
            </span>
            {account ? 'Edit ID' : 'Add ID'}
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit((values) => mutation.mutate(values))}
          className="flex min-h-0 flex-1 flex-col"
          autoComplete="off"
        >
          {/* Below md this is the one scroller and the columns stack. From md up each column handles
              its own overflow, so no scroller is ever nested in another. */}
          <div className="thin-scrollbar min-h-0 flex-1 overflow-y-auto md:grid md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:grid-rows-[minmax(0,1fr)] md:overflow-hidden">
            <div className="thin-scrollbar space-y-4 px-5 py-4 md:overflow-y-auto md:border-r md:border-border/60">
              <div className="space-y-1.5">
                <Label htmlFor="id-account-category">Category *</Label>
                <Controller
                  control={control}
                  name="category_id"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger id="id-account-category">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => (
                          <SelectItem key={c.id} value={c.id}><CategoryOption name={c.name} iconPath={c.icon_path} /></SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.category_id && <p className="text-xs text-destructive">{errors.category_id.message}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="id-account-identifier">Account ID *</Label>
                <Input
                  id="id-account-identifier"
                  autoComplete="off"
                  placeholder="e.g. name@example.com"
                  maxLength={255}
                  {...register('account_identifier')}
                />
                {errors.account_identifier && <p className="text-xs text-destructive">{errors.account_identifier.message}</p>}
              </div>
            </div>

            <div className="flex flex-col gap-2 border-t border-border/60 px-5 py-4 md:min-h-0 md:border-t-0">
              <div className="flex h-6 shrink-0 items-center justify-between gap-2">
                <Label>Users *</Label>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{selectedIds.length} selected</span>
                  {selectedIds.length > 0 && (
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() => setValue('user_ids', [], { shouldDirty: true })}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <p className="shrink-0 text-xs text-muted-foreground">
                Selected users see this ID on My IDs.
              </p>
              {errors.user_ids && <p className="shrink-0 text-xs text-destructive">{errors.user_ids.message}</p>}
              <Controller
                control={control}
                name="user_ids"
                render={({ field }) => (
                  <IdUserMultiPicker
                    people={options}
                    value={field.value}
                    onChange={field.onChange}
                    className="md:min-h-0 md:flex-1"
                  />
                )}
              />
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t border-border/60 px-5 py-3">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {account ? 'Save changes' : 'Save ID'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
